import * as React from "react";
import {Checkbox} from "@octopusdeploy/design-system-components";
import {FormBaseComponent, OptionalFormBaseComponentState} from "components/form/FormBaseComponent";
import FormPaperLayout from "components/FormPaperLayout/FormPaperLayout";
import {InternalRedirect} from "components/Navigation/InternalRedirect";
import routeLinks from "routeLinks";
import repository from "client/repository";
import Summary from "components/form/Sections/Summary";
import ExpandableFormSection from "components/form/Sections/ExpandableFormSection";
import {Text} from "components/form";
import {cloneDeep} from "lodash";
import { SearchTaskLogRequest } from "client/resources/SearchTaskLogRequest";
import { Callout } from "@octopusdeploy/design-system-components";
import ReefMultiSelect from "shared/ReefMultiSelect";
import {ReefResource} from "client/resources/reefResource";
import HostedInstanceMultiSelect from "shared/HostedInstanceMultiSelect";
import { HostedInstanceLookupResource } from "client/resources/hostedInstanceLookupResource";

interface State extends OptionalFormBaseComponentState<Partial<SearchTaskLogRequest>> {
    model: Partial<SearchTaskLogRequest>;
    cleanModel: Partial<SearchTaskLogRequest>;
    redirectTo?: string;
    reefs: ReefResource[];
    instances?: HostedInstanceLookupResource[];
}


class SearchTaskLogs extends FormBaseComponent<{}, State, Partial<SearchTaskLogRequest>> {
    constructor(props: {}) {
        super(props);

        const model: Partial<SearchTaskLogRequest> = {
            ReefIdsToSearch: [],
            RequestInstanceIds: [],
            FailTaskIfTooManySearchesFail: true
        };

        this.state = {
            model,
            cleanModel: cloneDeep(model),
            reefs: [],
            instances: []
        };
    }

    componentDidMount() {
        return this.doBusyTask(async () => {
            this.setState({
                reefs: await repository.Reefs.list(),
                instances: await repository.HostedInstances.lookup()
            });
        });
    }


    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo}/>;
        }

        return <FormPaperLayout
            title="Search Task Log"
            busy={this.state.busy}
            errors={this.state.errors}
            model={this.state.model}
            cleanModel={this.state.cleanModel}
            onSaveClick={() => this.handleSearchClick()}
            saveButtonBusyLabel={"Searching"}
            saveButtonLabel={"Search"}
            overFlowActions={null}
            expandAllOnMount={true}>
            {this.getBody()}
        </FormPaperLayout>;
    }

    getBody() {
        return <div>
            <Callout type="warning" title={"Use with caution!!!"}>
                <p>This searching process could take quite a while to complete and consume Instances' resources.</p>
            </Callout>
            <ExpandableFormSection
                errorKey="SearchTerm"
                title="Search Term"
                summary={Summary.summary("Search Term")}>
                <Text label="Search Term"
                        hintText="Enter plain text to search..."
                        value={this.state.model.SearchTerm}
                        onChange={(searchTerm: string) => this.setModelState({
                            SearchTerm: searchTerm
                        })}
                        autoFocus={true}
                />
                <Checkbox value={this.state.model.FailTaskIfTooManySearchesFail} onChange={v => this.setModelState({ FailTaskIfTooManySearchesFail: v })} label={"Fail task if too many searches fail"}/>
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="Reefs"
                title="Reefs To Search"
                summary={Summary.summary("Reefs")}>
                <ReefMultiSelect
                    items={this.state.reefs}
                    value={this.state.model.ReefIdsToSearch}
                    onChange={reefIds => this.setModelState({
                        ReefIdsToSearch: reefIds
                    })}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="Hosted Instances"
                title="Hosted Instances To Search"
                summary={Summary.summary("Hosted Instances")}>
                <HostedInstanceMultiSelect
                    items={this.state.instances}
                    value={this.state.model.RequestInstanceIds}
                    onChange={instanceIds => this.setModelState({
                        RequestInstanceIds: instanceIds
                    })}
                />
            </ExpandableFormSection>
        </div>;
    }

    handleSearchClick() {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.searchTaskLogs(this.state.model as SearchTaskLogRequest);
            this.setState({redirectTo: routeLinks.backgroundTasks.root});
        });
    }
}

export default SearchTaskLogs;
